import * as React from "react";
import {
    INTERACTION_PROCESS_NB,
    Policy,
    ProductsEnum,
    Quote,
} from "@ferry/journey-services";
import {authorize, IWithRouterProps, Overlay, withRouter} from "@athena-portal/components";
import {lazyInject} from "@athena-portal/core";
import {PluginTemplates} from "../../../../utils/PluginTemplates";
import FalconHeader from "../../../../components/navigation/FalconHeader";
import {BackButton} from "../../../../components/navigation/BackButton";
import {ILookupService, Types as JourneyCoreTypeDefs} from "@athena-portal/plugin-journey";
import {SuccessfulConfirmationModal} from "../../../../components/modals/SuccessfulConfirmationModal";
import {StorageSuccessModal} from "../../../../components/modals/StorageSuccessModal";
import {DriveAwayTiles} from "./DriveAwayTiles";
import {LVDataLayerHelper} from "../../../../utils/LVDataLayerHelper";
import {formatDate} from "@ferry/plugin-ferry-journey";
import {pushEventToAdobeDataLayerTimeout} from "../../../../utils/AdobeDataLayerHelper";
import {getCoverTierName} from "src/CommonFunctions";
import {LvToAllianzUKTransferDashboardBanner} from "../../../../components/banners/LvToAllianzUKTransferDashboardBanner";
import type {ICustomerPortalService} from "../../../../services/ICustomerPortalService";
import {Types as CustomerPortalTypes} from "../../../../Types";
import {getPrettifiedInteractionBrandName, FalconNavigatorHeader, Footer} from "@ferry/portal-core";

interface IParams {
    policyNumber: string;
    [key: string]: string | undefined
}

interface IState {
    policy?: Policy;
    policies?: Policy[];
    partyLoaded: boolean;
    hasMultiplePolicies?: boolean;
    quotes?: Quote[];
    quotesLoaded: boolean;
    claimSubmissionDate?: string;
    onlyAnnualMotorPolicyPresent: boolean;
    showFeedbackDialog: boolean;
    displayOverlay: boolean;
    classifiersLoaded: boolean;
    policiesProcessed: boolean;
    policyPropertiesLoaded: boolean;
    registrationNumber?: string;
}

@authorize
export class DriveAwayDashboardPagePlain extends React.Component<IWithRouterProps<IParams>, IState> {

    private pluginTemplates = new PluginTemplates();
    private showSuccessPopup: string | null;
    private successPopupTitle: string;
    private readonly modalContent: React.ReactNode;
    private referrerJourney: string | null;
    private readonly multiPolicyAccount: boolean;

    @lazyInject(CustomerPortalTypes.CustomerPortalService)
    private customerPortalService: ICustomerPortalService;

    @lazyInject(JourneyCoreTypeDefs.LookupService)
    private lookupService: ILookupService;

    private pushAdobeDataLayerTimeout: any;

    constructor(props: IWithRouterProps<IParams>) {
        super(props);

        this.state = {
            quotesLoaded: false,
            partyLoaded: false,
            onlyAnnualMotorPolicyPresent: false,
            showFeedbackDialog: false,
            displayOverlay: false,
            classifiersLoaded: false,
            policiesProcessed: false,
            policyPropertiesLoaded: false,
            registrationNumber: ''
        };

        this.multiPolicyAccount = window.sessionStorage.getItem("multiPolicyAccount") === "true";
    }

    public async componentDidMount() {
        const policyNumber: string = this.props.params.policyNumber;

        this.pluginTemplates.setPageTitle("Dashboard");

        this.customerPortalService.getPolicy(policyNumber).subscribe((policy: Policy) => {
            let vrn: string;
                vrn = policy.insuredVehicles[0].registrationNumber;

            this.setState({
                policy,
                policiesProcessed: true,
                registrationNumber: vrn
            })

            const successModalRef = window.sessionStorage.getItem("referrerJourney");
            if (window.sessionStorage.getItem("showPopup") === "true"
                && successModalRef === INTERACTION_PROCESS_NB && policy.interaction?.product === "DriveAwayInsurance") {
                this.pushAdobeDataLayerTimeout = pushEventToAdobeDataLayerTimeout(2000, "driveaway_completed");
            }
        });

        this.customerPortalService.getPolicies().subscribe((policies: Policy[])=> {
            if(policies.length > 1){
                this.setState({hasMultiplePolicies : true})
            }
        })

        this.customerPortalService.getQuotes().subscribe(
            (quotes: Quote[]) => {
                this.setState({
                    quotes,
                    quotesLoaded: true
                })
            }
        );

        this.lookupService.fetchLookupValues(`all${ProductsEnum.WOLF_MOTOR}`, "en-UK")
            .subscribe(() => {
                this.setState({classifiersLoaded: true})
            });
    }

    public componentWillUnmount() {
        clearTimeout(this.pushAdobeDataLayerTimeout);
    }

    public render() {
        const {
            policy,
            quotesLoaded,
            policiesProcessed
        } = this.state;
        const dataLoaded = policiesProcessed && (!policy || quotesLoaded);

        // Recreate DataLayer
        if (policy) {
            const interaction = policy.interaction;
            new LVDataLayerHelper().mapInteractionToDataLayer(interaction);
        }

        this.referrerJourney = window.sessionStorage.getItem("referrerJourney");
        this.showSuccessPopup = window.sessionStorage.getItem("showPopup");

        return (
            <>
                <Overlay show={!dataLoaded || this.state.displayOverlay || !this.state.classifiersLoaded}/>
                {this.showSuccessPopup && dataLoaded && (
                    this.renderSuccessfulConfirmationModal()
                )}
                <FalconHeader/>
                <div className="page-contents dashboard-page pt-3">
                    <FalconNavigatorHeader
                        back={{
                            link: "/dashboard",
                            hidden: !this.multiPolicyAccount
                        }}
                        title={this.multiPolicyAccount ? '5 Day Drive Away cover' : `Hi ${policy?.policyHolder.firstName ?? ""}`}>
                        {policy && <div
                            className="number-plate-preview mx-0 mb-4">{this.state.registrationNumber}</div>}
                    </FalconNavigatorHeader>
                    {dataLoaded && policy && (
                        <>
                            <DriveAwayTiles
                                policies={[policy]}
                                policyNumber={this.props.params.policyNumber}
                                hasMultiplePolicies={this.state.hasMultiplePolicies}
                                {...this.props}
                            />
                            <LvToAllianzUKTransferDashboardBanner />
                        </>
                    )}
                    {this.multiPolicyAccount && <BackButton isTop={false} link={"/dashboard/"}/>}
                </div>
                <Footer/>
            </>
        )
    }

    private renderSuccessfulConfirmationModal(): React.ReactNode {
        if (this.referrerJourney === INTERACTION_PROCESS_NB && this.state.policy!.interaction?.product === "DriveAwayInsurance") {
            return <SuccessfulConfirmationModal headerText={"All done"}
                                            modalContent={this.renderSuccessConfirmationPopup()}/>
        } else {
            return <StorageSuccessModal
                title={this.successPopupTitle}
                explanationWording={this.modalContent}
                anythingElseTitle={""}
                explanationAnythingElseWording={""}
            />
        }
    }

    private renderSuccessConfirmationPopup(): React.ReactNode {
        const policy: Policy = this.state.policy!;
        const policyStartDate: string = policy?.startDate;
        if (!policy) {
            return;
        }
        return (
            <div className="text-center">
                <p className="mb-1 font-weight-bold">Thanks {policy.policyHolder.firstName},</p>
                <p className="mt-4 mb-4">
                    <span className="font-weight-bold">You’re covered.</span>
                    <br/>
                    Your car 5-Day Drive Away car policy is now scheduled to start on {formatDate(policyStartDate, "DD MMM YYYY")}.
                    {
                        policy?.interaction?.factors?.amountToPay === 0 && (
                            <>
                                <br/>
                                The {getPrettifiedInteractionBrandName(policy.interaction.brandName)} Annual Car Insurance ({getCoverTierName(policy.interaction.coverTier)}) starts on {formatDate(policy?.interaction?.headerInfo?.startDate, "DD MMM YYYY")}.
                            </>
                        )
                    }
                </p>
                <p className="mb-4">When you’re ready head over to your account where you can check out your documents, manage your policy and retrieve your quote for annual car insurance.</p>
                <p className="mb-4">If you do need to contact us then head over to our help section for your options.</p>
                <p className="mb-0 font-weight-bold">Enjoy your new car.</p>

            </div>
        )
    }
}

export const DriveAwayDashboardPage = withRouter(DriveAwayDashboardPagePlain)
